import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import cx from "classnames";
import { v4 as uuidv4 } from "uuid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faChevronDown } from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";
import {
  buildLocationDisplayModeOptions,
  getLocationModeLabel,
  type LocationDisplayMode,
} from "../../../helpers/locationDisplayUtils";
import {
  locationDisplayModesEqual,
  useLocationDisplayMode,
} from "./LocationDisplayModeContext";

const LocationColumnHeader = () => {
  const { t } = useTranslation();
  const { activeMode, setActiveMode, tierCount } = useLocationDisplayMode();
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useMemo(() => `location-mode-menu-${uuidv4()}`, []);

  const options = buildLocationDisplayModeOptions(tierCount, t);
  const activeLabel = getLocationModeLabel(activeMode, tierCount, t);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const handleSelect = (mode: LocationDisplayMode) => {
    setActiveMode(mode);
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const menuPosition = (() => {
    if (!isOpen || !triggerRef.current) {
      return null;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    return { top: rect.bottom + 4, left: rect.left };
  })();

  return (
    <th
      scope="col"
      className="px-3 py-3 text-left text-sm font-semibold text-gray-900"
    >
      <button
        ref={triggerRef}
        type="button"
        className={cx(
          "inline-flex items-center gap-1.5 rounded px-1.5 py-1 -ml-1.5",
          "hover:bg-gray-100 transition-colors",
          isOpen && "bg-gray-100",
        )}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={menuId}
        onClick={() => setIsOpen((open) => !open)}
      >
        <span>Location</span>
        <span className="text-xs font-normal text-primary">{activeLabel}</span>
        <FontAwesomeIcon
          icon={faChevronDown}
          className={cx(
            "text-[10px] text-gray-400 transition-transform duration-150",
            isOpen && "rotate-180",
          )}
        />
      </button>

      {isOpen &&
        menuPosition &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="listbox"
            className="fixed z-[110] min-w-[10rem] max-h-64 overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg"
            style={{ top: menuPosition.top, left: menuPosition.left }}
          >
            {options.map((option) => {
              const isActive = locationDisplayModesEqual(option, activeMode);
              const label = getLocationModeLabel(option, tierCount, t);
              return (
                <button
                  key={
                    option.kind === "fullPath"
                      ? "fullPath"
                      : `tier-${option.tierIndex}`
                  }
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] hover:bg-gray-50"
                  onClick={() => handleSelect(option)}
                >
                  <FontAwesomeIcon
                    icon={faCheck}
                    className={cx(
                      "w-3 text-primary",
                      isActive ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className={cx(isActive && "text-primary")}>{label}</span>
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </th>
  );
};

export default LocationColumnHeader;
